import { 
  Brain,
  MessageSquare,
  BarChart3,
  Users,
  Bot,
  Monitor,
  Award,
  Smile,
  GraduationCap,
  Phone,
  Globe,
  Shield,
  Zap,
  Activity,
  Settings,
  Laptop,
  TrendingUp,
  Target,
  LineChart,
  Headphones,
  Mic,
  Video,
  FileText,
  Search,
  Clock,
  Lock,
  Database,
  Network,
  CheckCircle,
  Eye,
  Rocket,
  Sparkles,
  Workflow,
  Radio,
  Languages,
  LayoutDashboard,
  PieChart,
  AlertTriangle,
  UserCheck,
  Fingerprint,
  RefreshCw,
  Layers,
  GitBranch,
  Cpu,
  Gauge,
  Calendar,
  Bell,
  Share2,
  FileSearch,
  Lightbulb,
  Scale,
  ClipboardCheck,
  HeartHandshake,
  Megaphone,
  Wallet,
  CreditCard,
  Receipt,
  Building2,
  Map,
  Timer,
  Volume2,
  Voicemail,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  ScreenShare,
  MousePointer,
  Image,
  Play,
  Pause,
  SkipForward
} from 'lucide-react';
import type { ProductConfigs } from './types';

export const bpoContactCentreSolutionsConfigs: ProductConfigs = {
  // 1. AI Conversation Intelligence
  'ai-conversation-intelligence': {
    keyBenefits: [
      {
        icon: Brain,
        title: "Real-Time Speech-to-Text with 99% Accuracy",
        description: "Industry-leading transcription engine with dialect recognition, speaker diarization, and automatic punctuation for crystal-clear conversation capture.",
        metrics: "99.2% accuracy, <500ms latency",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Smile,
        title: "AI Emotion & Sentiment Detection",
        description: "Multi-dimensional sentiment analysis detecting frustration, satisfaction, urgency, and 25+ emotional states in real-time with confidence scoring.",
        metrics: "25+ emotions detected",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Target,
        title: "Customer Intent Recognition Engine",
        description: "Predictive AI identifies customer intents, purchase signals, and churn indicators before agents can, enabling proactive service delivery.",
        metrics: "94% intent accuracy",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Languages,
        title: "50+ Language Real-Time Support",
        description: "Native support for 50+ languages with regional dialect handling, code-switching detection, and cultural context awareness.",
        metrics: "50+ languages supported",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: AlertTriangle,
        title: "Compliance Keyword Monitoring",
        description: "Automated detection of compliance violations, prohibited phrases, and required disclosures with instant supervisor alerts.",
        metrics: "100% compliance coverage",
        color: "from-red-500 to-orange-600"
      },
      {
        icon: TrendingUp,
        title: "Competitive Intelligence Tracking",
        description: "Automatically identify and categorise competitor mentions, pricing discussions, and market positioning from conversations.",
        metrics: "Track 100+ competitors",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: BarChart3,
        title: "Topic Classification & Trending",
        description: "AI-powered topic extraction with trending analysis, emerging issue detection, and automated category assignment.",
        metrics: "95% classification accuracy",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Users,
        title: "Agent Performance Insights",
        description: "Objective scoring of agent soft skills, empathy levels, problem-solving approach, and adherence to best practices.",
        metrics: "20+ skill metrics",
        color: "from-cyan-500 to-blue-600"
      },
      {
        icon: Bell,
        title: "Real-Time Alert Configuration",
        description: "Customisable alert rules for escalations, sentiment drops, compliance flags, and VIP customer detection.",
        metrics: "Instant notifications",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: Layers,
        title: "Conversation Thread Analysis",
        description: "Track conversation journeys across channels with unified context, interaction history, and resolution tracking.",
        metrics: "360° conversation view",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Search,
        title: "Natural Language Search",
        description: "Search entire conversation archives using natural language queries, finding specific topics, phrases, or interaction patterns.",
        metrics: "Search millions in seconds",
        color: "from-teal-500 to-emerald-600"
      },
      {
        icon: LayoutDashboard,
        title: "Executive Intelligence Dashboard",
        description: "Real-time C-suite dashboard with KPI trending, benchmark comparisons, and AI-generated insight summaries.",
        metrics: "Live executive insights",
        color: "from-slate-500 to-gray-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: "Predictive Customer Journey Mapping",
        description: "AI predicts next-best-actions, churn probability, and upsell opportunities based on conversation patterns and historical data.",
        metrics: "87% prediction accuracy",
        color: "from-yellow-500 to-orange-600"
      },
      {
        icon: Workflow,
        title: "Automated Insight Distribution",
        description: "Push relevant insights to CRM, ticketing, and BI systems automatically with configurable triggers and data enrichment.",
        metrics: "50+ integration points",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Cpu,
        title: "Custom AI Model Training",
        description: "Train proprietary NLP models on your domain-specific vocabulary, products, and customer language patterns.",
        metrics: "Custom model deployment",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Video,
        title: "Video Interaction Analysis",
        description: "Extend speech analytics to video calls with facial expression analysis, visual sentiment, and engagement scoring.",
        metrics: "Full video intelligence",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Radio,
        title: "Real-Time Agent Whisper",
        description: "AI-powered suggestions whispered to agents in real-time including responses, next-best-actions, and knowledge articles.",
        metrics: "Sub-second suggestions",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: GitBranch,
        title: "Root Cause Analysis Engine",
        description: "Automatically identify systemic issues, recurring problems, and process failures from conversation pattern analysis.",
        metrics: "85% root cause identification",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: PieChart,
        title: "Voice of Customer Analytics",
        description: "Aggregate customer feedback, feature requests, and pain points into actionable VoC reports with sentiment trending.",
        metrics: "Comprehensive VoC insights",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Lightbulb,
        title: "AI Coaching Recommendations",
        description: "Generate personalised coaching plans for each agent based on conversation analysis and performance patterns.",
        metrics: "Automated coaching plans",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Share2,
        title: "Multi-Channel Unification",
        description: "Unified analytics across voice, chat, email, social, and video with cross-channel journey stitching.",
        metrics: "All channels unified",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: FileSearch,
        title: "Conversation Quality Scoring",
        description: "AI-generated quality scores for every interaction based on customisable criteria, industry standards, and best practices.",
        metrics: "100% auto-scoring",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Database,
        title: "Data Warehouse Export",
        description: "Structured export of all conversation data, metadata, and insights to enterprise data platforms for advanced analytics.",
        metrics: "Enterprise data integration",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Gauge,
        title: "Real-Time Performance Metrics",
        description: "Live dashboards showing conversation volume, sentiment trends, topic distributions, and agent performance in real-time.",
        metrics: "Sub-second updates",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'PCI-DSS Level 1 Compliant',
        description: 'Automatic PII and payment data redaction with PCI-DSS Level 1 certification for sensitive call handling',
        metrics: 'Highest PCI compliance',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'HIPAA & HITRUST Ready',
        description: 'Healthcare-grade compliance controls with BAA support and HITRUST certification for medical contact centres',
        metrics: 'Healthcare compliance ready',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 2. AI Quality Automation
  'ai-quality-automation': {
    keyBenefits: [
      {
        icon: Award,
        title: "100% Automated Quality Scoring",
        description: "AI evaluates every single interaction against your quality criteria, eliminating manual sampling and ensuring complete coverage.",
        metrics: "100% evaluation coverage",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Brain,
        title: "Intelligent Scorecard Builder",
        description: "Create sophisticated scorecards with weighted criteria, conditional logic, and AI-assisted question recommendations.",
        metrics: "Unlimited scorecards",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: AlertTriangle,
        title: "Real-Time Compliance Detection",
        description: "Instant flagging of compliance violations, missing disclosures, and regulatory risks during or after interactions.",
        metrics: "Zero compliance misses",
        color: "from-red-500 to-orange-600"
      },
      {
        icon: GitBranch,
        title: "Automated Root Cause Analysis",
        description: "AI identifies patterns in quality failures, linking issues to training gaps, process problems, or system limitations.",
        metrics: "90% root cause accuracy",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Scale,
        title: "Calibration Management",
        description: "AI-assisted calibration sessions with automated scoring comparisons, variance analysis, and consistency tracking.",
        metrics: "95% calibration accuracy",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Lightbulb,
        title: "AI Coaching Recommendations",
        description: "Personalised coaching suggestions generated automatically based on quality scores and improvement opportunities.",
        metrics: "Targeted coaching plans",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: TrendingUp,
        title: "Quality Trend Analytics",
        description: "Track quality scores over time with trend analysis, forecasting, and anomaly detection for proactive management.",
        metrics: "Predictive quality insights",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: BarChart3,
        title: "Benchmark Comparisons",
        description: "Compare quality performance against industry benchmarks, internal teams, and historical baselines.",
        metrics: "Industry benchmarking",
        color: "from-cyan-500 to-blue-600"
      },
      {
        icon: ClipboardCheck,
        title: "Appeal Workflow Management",
        description: "Structured appeal processes with evidence review, multi-level approvals, and resolution tracking.",
        metrics: "Fair dispute resolution",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: FileText,
        title: "Automated QA Reports",
        description: "Scheduled quality reports with executive summaries, detailed breakdowns, and actionable recommendations.",
        metrics: "Comprehensive reporting",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Users,
        title: "Multi-Evaluator Support",
        description: "Support for multiple QA analysts with workload distribution, bias detection, and inter-rater reliability tracking.",
        metrics: "Team-based QA",
        color: "from-teal-500 to-emerald-600"
      },
      {
        icon: Zap,
        title: "Real-Time Quality Alerts",
        description: "Instant notifications for critical quality failures, enabling immediate intervention and coaching opportunities.",
        metrics: "Instant alert delivery",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: "Natural Language Scoring",
        description: "Define quality criteria in plain English and let AI automatically translate them into scoring algorithms.",
        metrics: "No-code QA setup",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Automated Quality Workflows",
        description: "Trigger coaching sessions, training modules, or escalations automatically based on quality scores and patterns.",
        metrics: "500+ automation rules",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Video,
        title: "Screen Recording Quality Review",
        description: "Sync quality evaluations with screen recordings for comprehensive desktop procedure assessment.",
        metrics: "Full desktop review",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Languages,
        title: "Multi-Language QA Support",
        description: "Quality scoring in 50+ languages with cultural context awareness and region-specific compliance rules.",
        metrics: "Global QA coverage",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: PieChart,
        title: "Quality Driver Analysis",
        description: "AI identifies which quality factors most impact customer satisfaction, revenue, and retention outcomes.",
        metrics: "Outcome correlation",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Cpu,
        title: "Custom AI Model Training",
        description: "Train quality AI on your specific criteria, terminology, and evaluation standards for maximum accuracy.",
        metrics: "Domain-specific AI",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Share2,
        title: "CRM & LMS Integration",
        description: "Push quality data to CRM systems and trigger learning management assignments automatically.",
        metrics: "Seamless integration",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Search,
        title: "Quality Search & Discovery",
        description: "Find specific quality examples, best practices, or failure patterns across millions of evaluated interactions.",
        metrics: "Instant example search",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: LayoutDashboard,
        title: "Custom Dashboard Builder",
        description: "Build role-specific quality dashboards with drag-drop widgets, custom KPIs, and scheduled reports.",
        metrics: "Unlimited dashboards",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Gauge,
        title: "Real-Time Quality Wallboards",
        description: "Display live quality metrics on wallboards with gamification elements and team competitions.",
        metrics: "Live performance display",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: FileSearch,
        title: "Evidence Management",
        description: "Organised storage and retrieval of quality evidence including recordings, transcripts, and screenshots.",
        metrics: "Complete evidence trails",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: RefreshCw,
        title: "Continuous Quality Learning",
        description: "AI continuously improves scoring accuracy based on calibration feedback and human corrections.",
        metrics: "Self-improving AI",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'PCI-DSS Level 1 Compliant',
        description: 'Automatic PII and payment data redaction with PCI-DSS Level 1 certification for sensitive call handling',
        metrics: 'Highest PCI compliance',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'HIPAA & HITRUST Ready',
        description: 'Healthcare-grade compliance controls with BAA support and HITRUST certification for medical contact centres',
        metrics: 'Healthcare compliance ready',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 3. AI Coaching Platform
  'ai-coaching-platform': {
    keyBenefits: [
      {
        icon: Brain,
        title: "AI-Powered Coaching Recommendations",
        description: "Machine learning analyses performance patterns to generate personalised, actionable coaching recommendations for each agent.",
        metrics: "Personalised for each agent",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: GitBranch,
        title: "Dynamic Learning Paths",
        description: "Auto-generated learning journeys that adapt based on skill gaps, performance trends, and career development goals.",
        metrics: "Adaptive learning paths",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Target,
        title: "Skill Gap Analysis",
        description: "Comprehensive skill assessments with gap identification, priority ranking, and development timelines.",
        metrics: "Complete skill mapping",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Bell,
        title: "Real-Time Performance Alerts",
        description: "Instant notifications when agents need coaching intervention, celebrating wins, or preventing burnout.",
        metrics: "Proactive intervention",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Calendar,
        title: "1:1 Session Scheduling",
        description: "Integrated scheduling for coaching sessions with agenda templates, topic suggestions, and follow-up tracking.",
        metrics: "Streamlined scheduling",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: TrendingUp,
        title: "Goal Tracking & Progress",
        description: "SMART goal setting with automated progress tracking, milestone celebrations, and achievement badges.",
        metrics: "Visual progress tracking",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: FileText,
        title: "Best Practice Library",
        description: "Curated repository of top performer techniques, successful interaction examples, and proven strategies.",
        metrics: "1000+ best practices",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Users,
        title: "Peer Benchmarking",
        description: "Compare performance against peers, teams, and top performers with privacy-protected anonymisation.",
        metrics: "Fair peer comparison",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Award,
        title: "Gamification Engine",
        description: "Points, badges, leaderboards, and challenges that drive engagement and healthy competition.",
        metrics: "Complete gamification",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: LayoutDashboard,
        title: "Manager Coaching Dashboard",
        description: "Centralised view of team coaching needs, session history, and development progress for supervisors.",
        metrics: "Team oversight view",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Play,
        title: "Video Coaching Sessions",
        description: "Built-in video conferencing for remote coaching with screen sharing, recording, and annotation tools.",
        metrics: "Remote coaching ready",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: BarChart3,
        title: "Coaching Impact Analytics",
        description: "Measure the ROI of coaching activities with before/after performance comparisons and trending.",
        metrics: "Proven coaching ROI",
        color: "from-teal-500 to-emerald-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: "AI Conversation Coaching",
        description: "AI reviews conversation recordings and provides specific, timestamped coaching feedback on handling techniques.",
        metrics: "Conversation-level coaching",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Automated Coaching Triggers",
        description: "Auto-schedule coaching sessions based on quality scores, customer feedback, or performance thresholds.",
        metrics: "Trigger-based coaching",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: GraduationCap,
        title: "LMS Integration",
        description: "Deep integration with learning management systems for seamless training assignment and completion tracking.",
        metrics: "Unified learning ecosystem",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Mic,
        title: "Role-Play Simulations",
        description: "AI-powered practice scenarios where agents can rehearse difficult conversations with instant feedback.",
        metrics: "Safe practice environment",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Search,
        title: "Coachable Moment Search",
        description: "Find specific interaction examples that demonstrate learning opportunities or best practices.",
        metrics: "Instant example discovery",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: HeartHandshake,
        title: "Wellbeing Monitoring",
        description: "Track agent stress levels, burnout indicators, and engagement trends with intervention recommendations.",
        metrics: "Proactive wellbeing support",
        color: "from-rose-500 to-pink-600"
      },
      {
        icon: Share2,
        title: "Knowledge Sharing Network",
        description: "Social learning platform where top performers can share tips, strategies, and success stories.",
        metrics: "Peer-to-peer learning",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: PieChart,
        title: "Skill Certification Programs",
        description: "Create and manage skill certification paths with assessments, prerequisites, and digital credentials.",
        metrics: "Professional certification",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Lightbulb,
        title: "Manager Coaching for Coaches",
        description: "Train supervisors on effective coaching techniques with best practice guides and coaching the coach sessions.",
        metrics: "Coach development",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: RefreshCw,
        title: "Continuous Feedback Loop",
        description: "Real-time micro-feedback mechanisms that enable instant recognition and course correction.",
        metrics: "Continuous improvement",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: LayoutDashboard,
        title: "Executive Development Reports",
        description: "Strategic reports on workforce capability, skill coverage, and development investment returns.",
        metrics: "Strategic workforce insights",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Cpu,
        title: "Predictive Performance Modelling",
        description: "AI predicts future performance trajectories and recommends proactive development interventions.",
        metrics: "Predictive development",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'PCI-DSS Level 1 Compliant',
        description: 'Automatic PII and payment data redaction with PCI-DSS Level 1 certification for sensitive call handling',
        metrics: 'Highest PCI compliance',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'HIPAA & HITRUST Ready',
        description: 'Healthcare-grade compliance controls with BAA support and HITRUST certification for medical contact centres',
        metrics: 'Healthcare compliance ready',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 4. ESAT & CSAT Analytics
  'esat-csat-analytics': {
    keyBenefits: [
      {
        icon: Smile,
        title: "Multi-Channel Survey Distribution",
        description: "Deploy surveys via SMS, email, IVR, in-app, and web with smart timing and channel preference optimisation.",
        metrics: "8+ distribution channels",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Zap,
        title: "Real-Time CSAT Collection",
        description: "Capture customer satisfaction immediately after interactions with instant response streaming and alerting.",
        metrics: "<2 second collection",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Bell,
        title: "Pulse Survey Automation",
        description: "Automated employee pulse surveys with smart scheduling, fatigue prevention, and engagement tracking.",
        metrics: "Continuous feedback loop",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: TrendingUp,
        title: "NPS Tracking & Analysis",
        description: "Comprehensive Net Promoter Score tracking with promoter/detractor analysis and trend monitoring.",
        metrics: "Complete NPS insights",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Brain,
        title: "AI Sentiment Analysis",
        description: "Natural language processing extracts sentiment, themes, and actionable insights from open-text responses.",
        metrics: "95% sentiment accuracy",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: GitBranch,
        title: "Driver Analysis Engine",
        description: "Statistical analysis identifies which factors most impact satisfaction scores and prioritises improvements.",
        metrics: "Key driver identification",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: BarChart3,
        title: "Benchmark Comparisons",
        description: "Compare satisfaction scores against industry benchmarks, historical performance, and competitive standards.",
        metrics: "Industry benchmarking",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: FileText,
        title: "Custom Report Builder",
        description: "Drag-drop report creation with automated scheduling, role-based distribution, and export options.",
        metrics: "Unlimited custom reports",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Workflow,
        title: "Action Plan Workflows",
        description: "Convert survey insights into action plans with task assignment, progress tracking, and accountability.",
        metrics: "Insight to action",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: LayoutDashboard,
        title: "Executive Dashboards",
        description: "C-suite ready dashboards with KPI trending, executive summaries, and board-ready visualisations.",
        metrics: "Executive-ready insights",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Users,
        title: "Employee Engagement Index",
        description: "Comprehensive employee satisfaction scoring with eNPS, engagement drivers, and retention risk indicators.",
        metrics: "Complete ESAT picture",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: HeartHandshake,
        title: "Customer Effort Score",
        description: "Track customer effort across journeys with friction identification and experience optimisation insights.",
        metrics: "Effortless experience tracking",
        color: "from-teal-500 to-cyan-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: "Predictive Satisfaction Modelling",
        description: "AI predicts satisfaction outcomes before surveys based on interaction patterns and historical correlations.",
        metrics: "Predictive CSAT scores",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Target,
        title: "Journey-Based Surveying",
        description: "Deploy surveys at optimal points in customer journeys with contextual questions and touchpoint correlation.",
        metrics: "Journey-aware surveys",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Share2,
        title: "CRM & Ticketing Integration",
        description: "Push satisfaction data to CRM systems with automatic ticket creation for detractors and follow-up triggers.",
        metrics: "Closed-loop feedback",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Languages,
        title: "Multi-Language Surveys",
        description: "Deploy surveys in 50+ languages with cultural adaptation, RTL support, and regional customisation.",
        metrics: "Global survey reach",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: PieChart,
        title: "Text Analytics Dashboard",
        description: "Visual word clouds, theme clustering, and emerging topic detection from open-text feedback.",
        metrics: "Rich text insights",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: AlertTriangle,
        title: "Detractor Alert System",
        description: "Instant escalation for low satisfaction scores with automated recovery workflows and follow-up tracking.",
        metrics: "Instant detractor response",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Workflow,
        title: "Survey Logic Builder",
        description: "Sophisticated branching logic, skip patterns, and conditional questions for intelligent surveys.",
        metrics: "Smart survey flows",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Cpu,
        title: "Satisfaction Correlation Engine",
        description: "Automatically correlate satisfaction scores with operational metrics, quality scores, and business outcomes.",
        metrics: "Outcome correlation",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Search,
        title: "Feedback Search & Discovery",
        description: "Full-text search across all feedback with filters for sentiment, topics, scores, and time periods.",
        metrics: "Instant feedback access",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: RefreshCw,
        title: "Continuous Improvement Tracking",
        description: "Link action plans to satisfaction changes with before/after analysis and improvement attribution.",
        metrics: "Improvement measurement",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: LayoutDashboard,
        title: "Department Comparison Views",
        description: "Compare satisfaction across departments, teams, products, and channels with drill-down capabilities.",
        metrics: "Cross-organisational insights",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Gauge,
        title: "Real-Time Satisfaction Wallboards",
        description: "Display live satisfaction metrics on wallboards with trend indicators and celebration triggers.",
        metrics: "Live satisfaction display",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'GDPR & Privacy Compliant',
        description: 'Built-in consent management, data anonymisation, and right-to-be-forgotten workflows for privacy compliance',
        metrics: 'Privacy by design',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Anonymous Survey Options',
        description: 'True anonymity for employee surveys with cryptographic guarantees and zero-knowledge response collection',
        metrics: 'Guaranteed anonymity',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 5. Omnichannel Contact Hub
  'omnichannel-contact-hub': {
    keyBenefits: [
      {
        icon: Globe,
        title: "Unified Agent Desktop",
        description: "Single pane of glass for all channels with contextual customer data, interaction history, and AI assistance.",
        metrics: "One interface, all channels",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: MessageSquare,
        title: "Voice, Chat, Email & Social",
        description: "Native support for voice calls, live chat, email, SMS, and all major social media platforms.",
        metrics: "10+ channel types",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Phone,
        title: "WhatsApp Business Integration",
        description: "Official WhatsApp Business API integration with rich messaging, templates, and broadcast capabilities.",
        metrics: "Native WhatsApp support",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Workflow,
        title: "Intelligent Routing Engine",
        description: "Skills-based, AI-powered routing with priority queuing, load balancing, and customer preference matching.",
        metrics: "95% optimal routing",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Layers,
        title: "Conversation Continuity",
        description: "Seamless handoffs between channels with full context preservation and conversation threading.",
        metrics: "Zero context loss",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Sparkles,
        title: "AI Suggested Responses",
        description: "Real-time AI suggestions for agent responses based on context, knowledge base, and successful patterns.",
        metrics: "3x faster responses",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Languages,
        title: "Real-Time Translation",
        description: "AI-powered live translation enabling agents to communicate in 100+ languages seamlessly.",
        metrics: "100+ languages supported",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Users,
        title: "Smart Queue Management",
        description: "Visual queue monitoring with wait time predictions, abandonment prevention, and callback scheduling.",
        metrics: "Reduce abandonment 40%",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: BarChart3,
        title: "Channel Analytics",
        description: "Deep analytics for each channel with volume trending, performance comparisons, and optimisation insights.",
        metrics: "Complete channel insights",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Clock,
        title: "SLA Monitoring",
        description: "Real-time SLA tracking with breach predictions, escalation automation, and compliance reporting.",
        metrics: "99% SLA compliance",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Video,
        title: "Video Chat & Co-Browse",
        description: "Integrated video calling with screen sharing, co-browsing, and document sharing for complex support.",
        metrics: "Rich interaction support",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: Bot,
        title: "Seamless Bot Handoff",
        description: "Intelligent escalation from chatbots to human agents with full context transfer and sentiment awareness.",
        metrics: "Smooth AI-human transition",
        color: "from-blue-500 to-indigo-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "Predictive Intent Routing",
        description: "AI predicts customer intent from first contact and routes to the best-suited agent before conversation begins.",
        metrics: "Intent-based routing",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Visual Workflow Builder",
        description: "No-code routing workflow designer with drag-drop logic, conditional branching, and testing tools.",
        metrics: "No-code configuration",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Share2,
        title: "CRM Deep Integration",
        description: "Bi-directional sync with major CRMs for customer data, interaction logging, and 360° customer views.",
        metrics: "Seamless CRM sync",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Mic,
        title: "Voice Biometrics Authentication",
        description: "Secure customer authentication using voice biometrics for faster, friction-free verification.",
        metrics: "Secure voice ID",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Zap,
        title: "Proactive Outreach Engine",
        description: "Trigger outbound communications based on customer journey events, milestones, or predicted needs.",
        metrics: "Proactive engagement",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Search,
        title: "Knowledge Base Integration",
        description: "Contextual knowledge article suggestions, instant answer insertion, and AI-powered search.",
        metrics: "Instant knowledge access",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: PieChart,
        title: "Customer Journey Analytics",
        description: "Visualise complete customer journeys across channels with touchpoint analysis and friction detection.",
        metrics: "End-to-end journey view",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: LayoutDashboard,
        title: "Supervisor Command Centre",
        description: "Real-time operational dashboard with agent monitoring, queue health, and instant intervention tools.",
        metrics: "Complete oversight",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Gauge,
        title: "Capacity Planning Tools",
        description: "AI-powered capacity forecasting with scheduling recommendations and what-if scenario modelling.",
        metrics: "Optimised staffing",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Bell,
        title: "Smart Escalation Management",
        description: "Automated escalation based on sentiment, wait time, customer value, and interaction complexity.",
        metrics: "Intelligent escalation",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: RefreshCw,
        title: "Callback Scheduling",
        description: "Customer callback scheduling with confirmation, reminders, and slot management for reduced abandonment.",
        metrics: "Flexible callback options",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Cpu,
        title: "Custom Integration Framework",
        description: "APIs and webhooks for integrating with any business system, enabling unlimited customisation.",
        metrics: "Unlimited integrations",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'PCI-DSS Level 1 Compliant',
        description: 'Secure payment processing with PCI-DSS Level 1 certification and automatic card data masking',
        metrics: 'Highest PCI compliance',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'HIPAA & HITRUST Ready',
        description: 'Healthcare-grade compliance controls with BAA support and HITRUST certification for medical contact centres',
        metrics: 'Healthcare compliance ready',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 6. Performance Dashboards
  'performance-dashboards': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: "Real-Time KPI Widgets",
        description: "Live-updating widgets for all critical metrics including handle time, wait time, FCR, and customer satisfaction.",
        metrics: "Sub-second updates",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Users,
        title: "Agent Performance Cards",
        description: "Individual agent scorecards with daily, weekly, and monthly performance tracking against targets.",
        metrics: "Individual accountability",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Target,
        title: "Team Comparison Views",
        description: "Side-by-side team performance comparisons with drill-down capabilities and variance highlighting.",
        metrics: "Team benchmarking",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Activity,
        title: "Project Health Metrics",
        description: "Comprehensive project tracking with SLA compliance, volume trending, and client KPI dashboards.",
        metrics: "Project oversight",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: LayoutDashboard,
        title: "Custom Dashboard Builder",
        description: "Drag-drop dashboard creation with 100+ widget types, custom formulas, and role-based views.",
        metrics: "Unlimited customisation",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: FileText,
        title: "Scheduled Report Delivery",
        description: "Automated report generation and distribution via email, Teams, Slack, or file servers.",
        metrics: "Automated reporting",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Search,
        title: "Drill-Down Analytics",
        description: "Click any metric to explore underlying data with filters, time comparisons, and root cause analysis.",
        metrics: "Deep data exploration",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: TrendingUp,
        title: "Predictive Trending",
        description: "AI-powered forecasting shows where metrics are heading with confidence intervals and alerts.",
        metrics: "Predictive insights",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Monitor,
        title: "Wallboard Display Mode",
        description: "Optimised display mode for large screens with auto-rotation, visual alerts, and celebration animations.",
        metrics: "Contact floor displays",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Laptop,
        title: "Mobile-First Design",
        description: "Full dashboard functionality on mobile devices for managers and agents on-the-go.",
        metrics: "Access anywhere",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Bell,
        title: "Threshold Alerts",
        description: "Configurable alerts when KPIs breach thresholds with escalation rules and notification preferences.",
        metrics: "Proactive monitoring",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: Award,
        title: "Gamification Leaderboards",
        description: "Competitive leaderboards with points, badges, and achievements to drive engagement and performance.",
        metrics: "Motivational elements",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Insight Generation",
        description: "Natural language summaries of performance trends, anomalies, and recommendations generated automatically.",
        metrics: "AI-written insights",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Data Source Integration",
        description: "Connect to any data source including ACD, CRM, WFM, QA, and custom databases with real-time sync.",
        metrics: "Universal data access",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Sparkles,
        title: "Natural Language Query",
        description: "Ask questions in plain English like 'Show me yesterday's FCR by team' and get instant visualisations.",
        metrics: "Conversational BI",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: PieChart,
        title: "Advanced Visualisation Library",
        description: "Heatmaps, waterfall charts, Sankey diagrams, and 50+ visualisation types for any analysis need.",
        metrics: "Rich visualisations",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Share2,
        title: "Embedded Analytics API",
        description: "Embed dashboards and charts into other applications, portals, or client-facing reports.",
        metrics: "White-label embedding",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Layers,
        title: "Multi-Site Consolidation",
        description: "Roll up data from multiple sites, BPOs, and platforms into unified enterprise views.",
        metrics: "Enterprise visibility",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Gauge,
        title: "SLA Burn Rate Indicators",
        description: "Visual indicators showing SLA consumption rate with time-to-breach predictions.",
        metrics: "SLA health tracking",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: RefreshCw,
        title: "Historical Comparison Mode",
        description: "Overlay current performance against historical periods with automatic adjustment for seasonality.",
        metrics: "Period comparisons",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: FileSearch,
        title: "Ad-Hoc Report Builder",
        description: "Self-service report creation with drag-drop fields, filters, and export to Excel, PDF, or PowerPoint.",
        metrics: "Self-service analytics",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Cpu,
        title: "Custom Metric Calculator",
        description: "Create custom KPIs using formula builder with arithmetic, conditionals, and aggregations.",
        metrics: "Custom metrics",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: LayoutDashboard,
        title: "Executive Summary Views",
        description: "One-page executive summaries with traffic light indicators, key metrics, and narrative commentary.",
        metrics: "Board-ready reports",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Clock,
        title: "Scheduled Snapshot Exports",
        description: "Automated dashboard snapshots saved to file servers or cloud storage for compliance archives.",
        metrics: "Automated archiving",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Data Access Governance',
        description: 'Row-level security ensuring users only see data they are authorised to access based on hierarchy and role',
        metrics: 'Granular data security',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'IP Whitelisting & VPN Support',
        description: 'Restrict access by IP address ranges and support VPN-only connections for enhanced security',
        metrics: 'Network-level security',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 7. Predictive Analytics Suite
  'predictive-analytics-suite': {
    keyBenefits: [
      {
        icon: TrendingUp,
        title: "AI Volume Forecasting",
        description: "Machine learning models predict contact volumes with 95%+ accuracy across all channels and intervals.",
        metrics: "95%+ forecast accuracy",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: AlertTriangle,
        title: "Churn Risk Prediction",
        description: "Identify at-risk customers before they leave with churn probability scoring and intervention recommendations.",
        metrics: "85% churn prediction",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Users,
        title: "Attrition Modelling",
        description: "Predict agent attrition risk with retention recommendations and proactive intervention triggers.",
        metrics: "Reduce attrition 30%",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: CreditCard,
        title: "Revenue Impact Analysis",
        description: "Connect operational metrics to revenue outcomes with causality modelling and ROI predictions.",
        metrics: "Revenue attribution",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Calendar,
        title: "Capacity Planning Tools",
        description: "Scenario-based capacity planning with staffing recommendations and budget impact modelling.",
        metrics: "Optimal staffing",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: GitBranch,
        title: "What-If Scenario Builder",
        description: "Model hypothetical scenarios like volume spikes, staff changes, or process modifications.",
        metrics: "Unlimited scenarios",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Bell,
        title: "Anomaly Detection",
        description: "AI identifies unusual patterns in real-time with automated alerting and root cause suggestions.",
        metrics: "Instant anomaly alerts",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Cpu,
        title: "Custom ML Models",
        description: "Build and deploy custom machine learning models using your data with no-code model builder.",
        metrics: "Custom AI deployment",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Share2,
        title: "Data Integration Hub",
        description: "Connect to any data source with pre-built connectors for ACD, CRM, WFM, HRIS, and custom systems.",
        metrics: "Universal data access",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: LayoutDashboard,
        title: "Executive Insights Portal",
        description: "Strategic analytics dashboards with AI-generated narratives, trend summaries, and action recommendations.",
        metrics: "C-suite ready insights",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Target,
        title: "Propensity Modelling",
        description: "Predict customer behaviours like purchase likelihood, escalation probability, or channel preference.",
        metrics: "Behaviour prediction",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: Sparkles,
        title: "Next-Best-Action Engine",
        description: "AI recommends optimal actions for each customer interaction based on predicted outcomes.",
        metrics: "Intelligent recommendations",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AutoML Model Training",
        description: "Automated machine learning finds the best algorithms and parameters for your prediction challenges.",
        metrics: "Automated AI optimisation",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Model Deployment Pipeline",
        description: "Production-grade ML pipeline with version control, A/B testing, and automated retraining.",
        metrics: "Enterprise ML ops",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: PieChart,
        title: "Feature Importance Analysis",
        description: "Understand which factors drive predictions with explainable AI and feature contribution views.",
        metrics: "Explainable AI",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: RefreshCw,
        title: "Real-Time Model Scoring",
        description: "Score interactions in real-time for immediate action triggers and routing decisions.",
        metrics: "Real-time predictions",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Database,
        title: "Data Lake Integration",
        description: "Connect to enterprise data lakes and warehouses for comprehensive analytics on historical data.",
        metrics: "Big data analytics",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Gauge,
        title: "Model Performance Monitoring",
        description: "Continuous monitoring of model accuracy with drift detection and automated alerting.",
        metrics: "Model health tracking",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Lightbulb,
        title: "Natural Language Insights",
        description: "AI generates plain-English explanations of predictions, trends, and recommended actions.",
        metrics: "Human-readable AI",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Search,
        title: "Cohort Analysis Tools",
        description: "Group customers by behaviour patterns and track cohort performance over time.",
        metrics: "Cohort intelligence",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: LayoutDashboard,
        title: "Simulation Dashboard",
        description: "Interactive simulation interface for testing hypotheses and exploring data relationships.",
        metrics: "Hypothesis testing",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Share2,
        title: "API & Webhook Integration",
        description: "Real-time prediction APIs and webhook notifications for seamless system integration.",
        metrics: "System integration",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: FileText,
        title: "Automated Report Generation",
        description: "Scheduled analytical reports with predictions, insights, and recommendations delivered automatically.",
        metrics: "Automated analytics",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Layers,
        title: "Multi-Tenant Analytics",
        description: "Separate analytics environments for different clients with aggregated enterprise views.",
        metrics: "Client segregation",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Data Anonymisation',
        description: 'Automated PII detection and anonymisation for ML training data with differential privacy techniques',
        metrics: 'Privacy-preserving ML',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Model Governance Framework',
        description: 'Complete audit trails for model training, deployment, and predictions for regulatory compliance',
        metrics: 'AI governance ready',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 8. Agent Mobile Portal
  'agent-mobile-portal': {
    keyBenefits: [
      {
        icon: Calendar,
        title: "Mobile Schedule Access",
        description: "View shifts, breaks, and meetings anywhere with real-time sync and push notifications for changes.",
        metrics: "Instant schedule access",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: RefreshCw,
        title: "Shift Swap Marketplace",
        description: "Browse available shifts, request swaps with colleagues, and manage approvals all from your phone.",
        metrics: "Easy shift trading",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Clock,
        title: "Time-Off Requests",
        description: "Submit and track PTO, sick leave, and personal days with manager approval workflows.",
        metrics: "Self-service time off",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Wallet,
        title: "Real-Time Earnings View",
        description: "Track earnings, bonuses, and incentives with detailed breakdowns and payment projections.",
        metrics: "Financial transparency",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: BarChart3,
        title: "Personal Performance Scorecard",
        description: "View your KPIs, quality scores, and rankings against goals with trend visualisations.",
        metrics: "Performance visibility",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Bell,
        title: "Push Notifications",
        description: "Instant alerts for schedule changes, coaching sessions, announcements, and urgent updates.",
        metrics: "Never miss updates",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: MessageSquare,
        title: "Team Chat Integration",
        description: "Communicate with supervisors and colleagues through integrated chat with rich media support.",
        metrics: "Instant communication",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: GraduationCap,
        title: "Training Module Access",
        description: "Complete training courses, certifications, and skill assessments from your mobile device.",
        metrics: "Learn anywhere",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Megaphone,
        title: "News & Announcements",
        description: "Stay informed with company news, policy updates, and team announcements in a social feed.",
        metrics: "Stay connected",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Fingerprint,
        title: "Biometric Clock-In",
        description: "Secure clock-in/out with facial recognition or fingerprint verification and GPS location.",
        metrics: "Secure attendance",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Award,
        title: "Badges & Achievements",
        description: "View earned badges, achievements, and leaderboard standings with social sharing options.",
        metrics: "Gamified recognition",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: HeartHandshake,
        title: "Wellbeing Check-Ins",
        description: "Daily mood check-ins and wellbeing resources with confidential support access.",
        metrics: "Employee wellbeing",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Schedule Optimisation",
        description: "AI suggests optimal shift preferences based on your performance patterns and personal preferences.",
        metrics: "Smart scheduling",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Offline Mode Support",
        description: "Full functionality even without internet with automatic sync when connectivity returns.",
        metrics: "Work anywhere",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Laptop,
        title: "Remote Work Tools",
        description: "Complete remote agent toolkit with VPN status, connectivity checks, and troubleshooting guides.",
        metrics: "Remote-ready",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Target,
        title: "Goal Setting & Tracking",
        description: "Set personal development goals with progress tracking and achievement celebrations.",
        metrics: "Personal development",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Search,
        title: "Knowledge Base Search",
        description: "Search company knowledge base and policies from your mobile with AI-powered answers.",
        metrics: "Instant knowledge",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Share2,
        title: "Benefits & HR Access",
        description: "View benefits information, submit HR requests, and access employee self-service portals.",
        metrics: "HR self-service",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: PieChart,
        title: "Personal Analytics Dashboard",
        description: "Deep dive into your own performance data with customisable charts and export options.",
        metrics: "Personal insights",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Lightbulb,
        title: "Idea Submission Portal",
        description: "Submit improvement ideas and suggestions with voting and implementation tracking.",
        metrics: "Employee voice",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: LayoutDashboard,
        title: "Widget Customisation",
        description: "Personalise your home screen with widgets for the metrics and features you use most.",
        metrics: "Personalised experience",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Gauge,
        title: "Incentive Calculators",
        description: "Calculate potential bonuses and incentives based on different performance scenarios.",
        metrics: "Motivation tools",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: Languages,
        title: "Multi-Language Support",
        description: "Full app localisation in 30+ languages with regional date, time, and number formats.",
        metrics: "Global accessibility",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Zap,
        title: "Quick Actions",
        description: "One-tap actions for common tasks like swap requests, schedule views, and clock operations.",
        metrics: "Fast interactions",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with secure keychain storage',
        metrics: 'Mobile-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade mobile backend with offline support and guaranteed availability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Biometric Authentication',
        description: 'Face ID, Touch ID, and fingerprint authentication with secure device attestation',
        metrics: 'Secure biometric access',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Remote Wipe Capability',
        description: 'IT administrators can remotely wipe app data if device is lost or employee leaves',
        metrics: 'Device security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all app actions, logins, and data access for compliance',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Secure Local Storage',
        description: 'Encrypted local data storage with automatic purge policies and no persistent PII',
        metrics: 'Safe offline data',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Certificate Pinning',
        description: 'SSL certificate pinning prevents man-in-the-middle attacks on mobile networks',
        metrics: 'Network security',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'App Store Compliance',
        description: 'Full compliance with Apple App Store and Google Play security and privacy guidelines',
        metrics: 'Platform compliant',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 9. Manager Command Centre
  'manager-command-centre': {
    keyBenefits: [
      {
        icon: Users,
        title: "Real-Time Team Overview",
        description: "Live dashboard showing all agent statuses, current activities, queue positions, and performance metrics.",
        metrics: "Complete team visibility",
        color: "from-slate-500 to-gray-600"
      },
      {
        icon: ClipboardCheck,
        title: "Exception Approvals",
        description: "One-tap approval for time-off requests, schedule changes, and policy exceptions with audit trail.",
        metrics: "Streamlined approvals",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Calendar,
        title: "Schedule Override Controls",
        description: "Make real-time schedule adjustments with impact preview and automatic notification to affected agents.",
        metrics: "Flexible scheduling",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Activity,
        title: "Agent Status Monitoring",
        description: "Monitor agent states with adherence tracking, productivity alerts, and presence management.",
        metrics: "Real-time monitoring",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: AlertTriangle,
        title: "Escalation Management",
        description: "Receive, prioritise, and manage escalations with SLA tracking and resolution workflows.",
        metrics: "Escalation control",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: FileText,
        title: "Mobile Coaching Notes",
        description: "Record coaching observations, feedback, and action items on the go with agent profiles.",
        metrics: "Instant documentation",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Bell,
        title: "Performance Alerts",
        description: "Push notifications for SLA breaches, agent issues, queue spikes, and exceptional performance.",
        metrics: "Proactive alerts",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Clock,
        title: "Intraday Management",
        description: "Real-time shrinkage tracking, skill reassignment, and volume rebalancing tools.",
        metrics: "Intraday control",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Settings,
        title: "Capacity Adjustments",
        description: "Quickly add overtime, manage voluntary time off, and adjust staffing based on real-time demand.",
        metrics: "Capacity control",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Globe,
        title: "Cross-Team Visibility",
        description: "View and support other teams, share resources, and coordinate across the organisation.",
        metrics: "Enterprise coordination",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: BarChart3,
        title: "Manager Scorecard",
        description: "Personal dashboard showing team KPIs, rankings, and progress against management objectives.",
        metrics: "Management accountability",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: MessageSquare,
        title: "Team Communication",
        description: "Broadcast messages, start group chats, and communicate urgent updates to team members.",
        metrics: "Team messaging",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Recommendations Engine",
        description: "AI suggests staffing adjustments, coaching priorities, and process improvements based on patterns.",
        metrics: "Intelligent suggestions",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Automated Workflow Triggers",
        description: "Set up automated responses to events like SLA warnings, queue spikes, or adherence issues.",
        metrics: "Automated management",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Mic,
        title: "Listen & Whisper Controls",
        description: "Remote silent monitoring and whisper coaching of live calls with quality marking.",
        metrics: "Remote coaching",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Target,
        title: "Goal Assignment & Tracking",
        description: "Set individual and team goals with progress tracking and achievement notifications.",
        metrics: "Goal management",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: PieChart,
        title: "Comparative Analytics",
        description: "Compare team performance against other teams, sites, or historical benchmarks.",
        metrics: "Performance benchmarks",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Gauge,
        title: "Real-Time Skill Management",
        description: "Instantly modify agent skill assignments to respond to queue demand changes.",
        metrics: "Dynamic skill routing",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Lightbulb,
        title: "Coaching Session Scheduler",
        description: "Schedule 1:1s with intelligent slot suggestions based on agent schedules and performance needs.",
        metrics: "Smart scheduling",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Share2,
        title: "Resource Sharing Tools",
        description: "Request and offer agent resources to other teams with approval workflows and impact analysis.",
        metrics: "Resource coordination",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: LayoutDashboard,
        title: "Custom Widget Dashboard",
        description: "Personalise your command centre with drag-drop widgets for the data you need most.",
        metrics: "Personalised interface",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Video,
        title: "Video Huddle Launch",
        description: "Instantly start video team huddles with screen sharing and meeting recording.",
        metrics: "Virtual meetings",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: FileSearch,
        title: "Interaction Search",
        description: "Search for specific agent interactions by customer, topic, or outcome for coaching material.",
        metrics: "Coaching examples",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Zap,
        title: "One-Tap Actions",
        description: "Quick action buttons for common management tasks like approvals, alerts, and reassignments.",
        metrics: "Fast management",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with secure keychain storage',
        metrics: 'Mobile-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade mobile backend with offline support and guaranteed availability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Management-Level Access Control',
        description: 'Hierarchical permissions ensuring managers only see data for their span of control',
        metrics: 'Hierarchical security',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all management actions, approvals, and data access for compliance',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Secure Action Logging',
        description: 'Every management action is logged with user, timestamp, and business justification',
        metrics: 'Complete accountability',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Session Management',
        description: 'Automatic session timeout, device management, and forced logout capabilities for lost devices',
        metrics: 'Session security',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 10. Softphone Pro
  'softphone-pro': {
    keyBenefits: [
      {
        icon: Volume2,
        title: "HD Voice Quality",
        description: "Crystal-clear audio with wideband codecs, acoustic echo cancellation, and noise suppression technology.",
        metrics: "Enterprise-grade audio",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Globe,
        title: "WebRTC Browser-Based",
        description: "No downloads required - make and receive calls directly from Chrome, Firefox, or Edge browsers.",
        metrics: "Zero installation",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: PhoneCall,
        title: "Click-to-Dial from CRM",
        description: "One-click dialling from any CRM, browser, or application with automatic screen pop and logging.",
        metrics: "Integrated calling",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: PhoneOutgoing,
        title: "Auto-Dialler Integration",
        description: "Seamless integration with preview, progressive, and predictive dialler campaigns.",
        metrics: "Campaign-ready",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Users,
        title: "Call Transfer & Conference",
        description: "Warm and blind transfers, multi-party conferencing, and supervisor consultation with one click.",
        metrics: "Advanced call control",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Voicemail,
        title: "Visual Voicemail",
        description: "View voicemail transcriptions, manage messages, and set personalised greetings from the interface.",
        metrics: "Modern voicemail",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Mic,
        title: "Call Recording Controls",
        description: "Start, stop, and pause call recording with compliance controls and PCI pause functionality.",
        metrics: "Recording management",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Activity,
        title: "Presence & Status",
        description: "Rich presence states synced across all channels with automatic DND and focus mode support.",
        metrics: "Status management",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Search,
        title: "Contact Directory",
        description: "Integrated corporate directory with search, favourites, and recent contacts for quick dialling.",
        metrics: "Instant access",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Laptop,
        title: "Mobile Companion App",
        description: "iOS and Android apps for work-from-anywhere calling with the same number and features.",
        metrics: "Mobility included",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: PhoneIncoming,
        title: "Intelligent Call Routing",
        description: "Skills-based routing, time-of-day rules, and failover handling for incoming calls.",
        metrics: "Smart call handling",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: BarChart3,
        title: "Call Analytics Dashboard",
        description: "Real-time and historical call metrics with call quality monitoring and reporting.",
        metrics: "Call insights",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Call Transcription",
        description: "Real-time transcription of calls with speaker identification and searchable archives.",
        metrics: "Live transcription",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Sparkles,
        title: "AI Next-Best-Action",
        description: "Real-time suggestions during calls based on conversation context and customer history.",
        metrics: "In-call coaching",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Workflow,
        title: "IVR Integration",
        description: "Seamless integration with IVR systems including DTMF control and automated workflows.",
        metrics: "IVR connectivity",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Share2,
        title: "CRM Screen Pop",
        description: "Automatic customer data pop-up before call connection with customisable layouts.",
        metrics: "Context-ready calls",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Gauge,
        title: "Call Quality Monitoring",
        description: "Real-time MOS score tracking, jitter, latency, and packet loss monitoring with alerts.",
        metrics: "Quality assurance",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Clock,
        title: "Scheduled Callbacks",
        description: "Schedule callbacks for customers with automated dialling at the specified time.",
        metrics: "Callback automation",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: AlertTriangle,
        title: "Emergency Calling (E911)",
        description: "Full E911 support with location tracking, emergency notifications, and compliance logging.",
        metrics: "Emergency ready",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Settings,
        title: "Audio Device Management",
        description: "Hot-swap between headsets, speakers, and microphones with automatic device detection.",
        metrics: "Hardware flexibility",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Layers,
        title: "Multi-Line Support",
        description: "Handle multiple simultaneous calls with call parking, hold, and line selection.",
        metrics: "Multi-call handling",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: PieChart,
        title: "Call Wrap-Up Automation",
        description: "Automated disposition codes, wrap-up timers, and post-call workflow triggers.",
        metrics: "Efficient wrap-up",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: LayoutDashboard,
        title: "Supervisor Dashboard",
        description: "Real-time agent call monitoring, barge-in, and whisper coaching capabilities.",
        metrics: "Management tools",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: RefreshCw,
        title: "Failover & Redundancy",
        description: "Automatic failover to backup connections with seamless call continuity.",
        metrics: "High availability",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'SRTP media encryption and TLS signalling encryption for completely secure voice communications',
        metrics: 'Encrypted voice',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Geo-redundant SIP infrastructure with automatic failover and carrier-grade reliability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'PCI-DSS Level 1 Compliant',
        description: 'Secure payment processing with call recording pause for card data and automatic masking',
        metrics: 'Highest PCI compliance',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Call Logging',
        description: 'Comprehensive CDRs with metadata, quality metrics, and interaction details for compliance',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Infrastructure',
        description: 'Multiple SIP endpoints across regions with automatic failover and disaster recovery',
        metrics: 'Global redundancy',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Fraud Detection',
        description: 'Real-time fraud monitoring for toll fraud, call patterns, and suspicious activity',
        metrics: 'Fraud protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'STIR/SHAKEN Compliant',
        description: 'Full STIR/SHAKEN caller ID authentication for spam and spoofing protection',
        metrics: 'Caller authentication',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 11. Case Management System
  'case-management-system': {
    keyBenefits: [
      {
        icon: Workflow,
        title: "Smart Case Routing",
        description: "AI-powered case assignment based on skills, workload, priority, and customer value with automatic escalation.",
        metrics: "Optimal case distribution",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: Clock,
        title: "SLA Management Engine",
        description: "Configurable SLA rules with milestone tracking, breach predictions, and automated escalations.",
        metrics: "99% SLA compliance",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: AlertTriangle,
        title: "Multi-Level Escalation",
        description: "Automated escalation paths based on time, severity, customer tier, and resolution complexity.",
        metrics: "Intelligent escalation",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Users,
        title: "Customer 360° View",
        description: "Complete customer profile with interaction history, cases, purchases, and sentiment trends.",
        metrics: "Unified customer view",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Settings,
        title: "Automated Workflows",
        description: "No-code workflow builder for case routing, approvals, notifications, and status updates.",
        metrics: "Process automation",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Search,
        title: "Knowledge Base Integration",
        description: "Contextual article suggestions, one-click insertion, and AI-powered search during case handling.",
        metrics: "Instant knowledge access",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: MessageSquare,
        title: "Email-to-Case Automation",
        description: "Automatic case creation from emails with AI categorisation, priority detection, and routing.",
        metrics: "Automated intake",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Layers,
        title: "Case Merge & Split",
        description: "Consolidate duplicate cases and split complex issues into sub-cases with linked tracking.",
        metrics: "Flexible case management",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: FileText,
        title: "Audit Trail & History",
        description: "Complete case history with all actions, updates, communications, and time stamps for compliance.",
        metrics: "Full audit visibility",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: BarChart3,
        title: "Resolution Analytics",
        description: "Deep analytics on resolution times, first-contact resolution, and agent performance by case type.",
        metrics: "Performance insights",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Bell,
        title: "Customer Notifications",
        description: "Automated updates to customers via email, SMS, or portal with status tracking and satisfaction surveys.",
        metrics: "Proactive communication",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Globe,
        title: "Self-Service Portal",
        description: "Customer portal for case submission, status tracking, knowledge search, and community forums.",
        metrics: "Customer self-service",
        color: "from-teal-500 to-cyan-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Case Classification",
        description: "Automatic case categorisation, priority assignment, and routing recommendations using NLP.",
        metrics: "95% classification accuracy",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Sparkles,
        title: "AI Resolution Suggestions",
        description: "Machine learning suggests resolution steps based on similar successful case patterns.",
        metrics: "Faster resolution",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: GitBranch,
        title: "Root Cause Analysis",
        description: "Identify systemic issues from case patterns with automated problem tracking and resolution.",
        metrics: "Systemic improvement",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: PieChart,
        title: "Case Pattern Analytics",
        description: "Discover trends, seasonal patterns, and emerging issues from case data with predictive alerts.",
        metrics: "Trend detection",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Share2,
        title: "Omnichannel Case Creation",
        description: "Create cases from any channel—phone, chat, email, social, or web—with unified tracking.",
        metrics: "Channel-agnostic",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Workflow,
        title: "Approval Workflows",
        description: "Multi-level approval processes for refunds, credits, exceptions, and policy overrides.",
        metrics: "Controlled approvals",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Cpu,
        title: "Custom Field Builder",
        description: "Create custom fields, forms, and case types for different products, services, or workflows.",
        metrics: "Complete flexibility",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: LayoutDashboard,
        title: "Queue Management Dashboard",
        description: "Real-time queue visibility with filters, bulk actions, and workload balancing tools.",
        metrics: "Queue control",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Languages,
        title: "Multi-Language Support",
        description: "Cases and communications in 50+ languages with AI translation and localised templates.",
        metrics: "Global support",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: RefreshCw,
        title: "Case Templates",
        description: "Pre-defined templates for common case types with automatic field population and workflows.",
        metrics: "Standardised handling",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: Gauge,
        title: "Priority Scoring Engine",
        description: "Dynamic priority calculation based on customer value, issue severity, and business impact.",
        metrics: "Smart prioritisation",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Lightbulb,
        title: "Predictive Case Resolution",
        description: "AI predicts resolution time, required effort, and likelihood of escalation for each case.",
        metrics: "Resolution prediction",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'PII Data Protection',
        description: 'Automatic detection and masking of sensitive data with field-level encryption options',
        metrics: 'Data privacy controls',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Data Retention Policies',
        description: 'Configurable retention rules with automated purge, legal hold support, and compliance reporting',
        metrics: 'Retention compliance',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 12. Real-Time BI Reporting
  'real-time-bi-reporting': {
    keyBenefits: [
      {
        icon: Activity,
        title: "Real-Time Data Streaming",
        description: "Live data feeds from all sources with sub-second latency for true real-time decision making.",
        metrics: "<1 second latency",
        color: "from-fuchsia-500 to-pink-600"
      },
      {
        icon: FileText,
        title: "Custom Report Builder",
        description: "Drag-drop report creation with 100+ chart types, filters, calculations, and formatting options.",
        metrics: "Unlimited reports",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: PieChart,
        title: "Drag-Drop Visualisations",
        description: "Create stunning visualisations with an intuitive interface—no coding or BI expertise required.",
        metrics: "No-code analytics",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Calendar,
        title: "Scheduled Report Delivery",
        description: "Automated report generation and distribution via email, Teams, Slack, or file servers.",
        metrics: "Automated delivery",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Database,
        title: "Data Warehouse Integration",
        description: "Native connectors for Snowflake, BigQuery, Redshift, and major data warehouse platforms.",
        metrics: "Enterprise data access",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Layers,
        title: "Multi-Source Data Blending",
        description: "Combine data from multiple sources with automatic joins, transformations, and data modelling.",
        metrics: "Unified analytics",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Users,
        title: "Role-Based Access",
        description: "Data access controls ensuring users see only what they're authorised to view based on role and hierarchy.",
        metrics: "Secure data access",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Share2,
        title: "Export to Excel/PDF",
        description: "One-click export to Excel, PDF, PowerPoint, and CSV with formatting preservation.",
        metrics: "Flexible export",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: LayoutDashboard,
        title: "Embedded Analytics",
        description: "Embed reports and dashboards into portals, applications, and client-facing interfaces.",
        metrics: "White-label embedding",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Cpu,
        title: "API Access for External BI",
        description: "REST APIs for data extraction to feed Tableau, Power BI, or custom analytics solutions.",
        metrics: "API connectivity",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Search,
        title: "Natural Language Query",
        description: "Ask questions in plain English and get instant visualisations and answers.",
        metrics: "Conversational BI",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Bell,
        title: "Alert & Threshold Monitoring",
        description: "Configurable alerts when metrics breach thresholds with escalation and notification options.",
        metrics: "Proactive monitoring",
        color: "from-teal-500 to-cyan-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Insight Generation",
        description: "Automatic discovery of trends, anomalies, and correlations with natural language explanations.",
        metrics: "AI-driven insights",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Sparkles,
        title: "Predictive Analytics Module",
        description: "Built-in forecasting, trend prediction, and what-if analysis without coding.",
        metrics: "Predictive capabilities",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Workflow,
        title: "Data Pipeline Automation",
        description: "Scheduled ETL processes with data quality checks, transformation rules, and error handling.",
        metrics: "Automated data prep",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: RefreshCw,
        title: "Incremental Refresh",
        description: "Efficient data updates that only refresh changed records for faster performance.",
        metrics: "Optimised performance",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Gauge,
        title: "Performance Optimisation",
        description: "Query optimisation, caching, and aggregation for sub-second response on large datasets.",
        metrics: "Blazing fast queries",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: GitBranch,
        title: "Version Control",
        description: "Full version history for reports and datasets with rollback, comparison, and audit trails.",
        metrics: "Change management",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Globe,
        title: "Multi-Tenant Architecture",
        description: "Separate environments for different clients or departments with unified administration.",
        metrics: "Client isolation",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Lightbulb,
        title: "Calculated Field Builder",
        description: "Create custom metrics with formula builder including DAX-like functions and conditional logic.",
        metrics: "Custom calculations",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Map,
        title: "Geospatial Analytics",
        description: "Map visualisations with heat maps, choropleth, and location-based analysis capabilities.",
        metrics: "Location intelligence",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Languages,
        title: "Multi-Language Localisation",
        description: "Reports and interface in 30+ languages with regional formatting for global deployment.",
        metrics: "Global accessibility",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: FileSearch,
        title: "Data Catalogue",
        description: "Searchable data catalogue with descriptions, lineage, and quality metrics for data governance.",
        metrics: "Data discovery",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Target,
        title: "Goal Tracking Widgets",
        description: "Visual goal tracking with progress indicators, targets, and variance highlighting.",
        metrics: "Goal visualisation",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Row-Level Security',
        description: 'Data filtering at the row level ensuring users only see data they are authorised to access',
        metrics: 'Granular data security',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Data Masking',
        description: 'Automatic masking of sensitive data fields based on user roles and data classification',
        metrics: 'Sensitive data protection',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 13. Audit & Compliance Manager
  'audit-compliance-manager': {
    keyBenefits: [
      {
        icon: Shield,
        title: "Automated Compliance Checks",
        description: "Continuous automated monitoring of compliance requirements with real-time violation detection and alerts.",
        metrics: "100% compliance monitoring",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Lock,
        title: "GDPR Consent Management",
        description: "Complete consent lifecycle management with preference centres, audit trails, and withdrawal handling.",
        metrics: "Full GDPR compliance",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: CreditCard,
        title: "PCI-DSS Controls",
        description: "Automated PCI-DSS control monitoring, call recording pause, and cardholder data protection.",
        metrics: "PCI Level 1 ready",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Clock,
        title: "Data Retention Automation",
        description: "Configurable retention policies with automated purge, legal hold, and compliance reporting.",
        metrics: "Automated data lifecycle",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: FileText,
        title: "Audit Trail Logging",
        description: "Comprehensive, tamper-proof logging of all user actions, data access, and system events.",
        metrics: "Complete audit visibility",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: AlertTriangle,
        title: "Risk Assessment Tools",
        description: "Built-in risk assessment frameworks with scoring, mitigation tracking, and heat map visualisation.",
        metrics: "Proactive risk management",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: LayoutDashboard,
        title: "Compliance Dashboards",
        description: "Real-time compliance status dashboards with drill-down capabilities and executive summaries.",
        metrics: "Instant compliance view",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: ClipboardCheck,
        title: "Evidence Collection",
        description: "Automated collection and organisation of compliance evidence for audits and assessments.",
        metrics: "Audit-ready evidence",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: FileSearch,
        title: "Policy Management",
        description: "Centralised policy repository with version control, acknowledgement tracking, and distribution.",
        metrics: "Policy governance",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: BarChart3,
        title: "Regulatory Reporting",
        description: "Pre-built report templates for regulators with automated generation and delivery scheduling.",
        metrics: "Regulatory compliance",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Users,
        title: "Staff Training Tracking",
        description: "Monitor compliance training completion, certification status, and knowledge assessments.",
        metrics: "Training compliance",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Bell,
        title: "Violation Alerting",
        description: "Real-time alerts for compliance violations with escalation workflows and remediation tracking.",
        metrics: "Instant violation alerts",
        color: "from-teal-500 to-cyan-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Compliance Scanning",
        description: "Natural language processing analyses conversations for compliance risks, script adherence, and violations.",
        metrics: "100% interaction scanning",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Remediation Workflows",
        description: "Automated remediation task assignment, tracking, and verification for compliance issues.",
        metrics: "Systematic remediation",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Scale,
        title: "Multi-Framework Support",
        description: "Pre-built controls for GDPR, PCI-DSS, ISO 27001, SOC 2, HIPAA, and custom frameworks.",
        metrics: "Multi-standard compliance",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Share2,
        title: "GRC Integration",
        description: "Integration with governance, risk, and compliance platforms for enterprise-wide visibility.",
        metrics: "Enterprise GRC",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Gauge,
        title: "Compliance Scoring",
        description: "Automated compliance scoring with trending, benchmarking, and improvement recommendations.",
        metrics: "Compliance metrics",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: PieChart,
        title: "Control Effectiveness Analysis",
        description: "Measure and report on the effectiveness of compliance controls with gap analysis.",
        metrics: "Control performance",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Calendar,
        title: "Audit Scheduling",
        description: "Plan and schedule compliance audits with resource allocation and timeline management.",
        metrics: "Audit planning",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Layers,
        title: "Vendor Compliance Management",
        description: "Track and manage compliance requirements for third-party vendors and suppliers.",
        metrics: "Vendor oversight",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Lightbulb,
        title: "Regulatory Change Monitoring",
        description: "Automated monitoring of regulatory changes with impact assessment and action planning.",
        metrics: "Regulatory intelligence",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: GitBranch,
        title: "Control Mapping",
        description: "Map controls across multiple frameworks to identify overlaps and reduce duplication.",
        metrics: "Efficient control management",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: Cpu,
        title: "Custom Control Builder",
        description: "Create and configure custom compliance controls for unique regulatory requirements.",
        metrics: "Flexible controls",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: RefreshCw,
        title: "Continuous Control Monitoring",
        description: "Real-time monitoring of control effectiveness with automated testing and alerting.",
        metrics: "Always-on compliance",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption keys',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling, global CDN, and guaranteed availability for mission-critical operations',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Tamper-Proof Audit Logs',
        description: 'Cryptographically signed audit logs ensuring integrity and non-repudiation for compliance evidence',
        metrics: 'Immutable audit records',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all data access, exports, and system changes for compliance and security monitoring',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance requirements',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered security monitoring with intrusion detection, anomaly alerts, and automated threat response',
        metrics: 'Proactive security monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Legal Hold Management',
        description: 'Preserve data for litigation or regulatory investigation with automated hold application and release',
        metrics: 'Litigation readiness',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 14. User & Access Management
  'user-access-management': {
    keyBenefits: [
      {
        icon: Users,
        title: "Role-Based Access Control",
        description: "Granular permission management with hierarchical roles, inheritance, and least-privilege enforcement.",
        metrics: "Granular permissions",
        color: "from-sky-500 to-blue-600"
      },
      {
        icon: Globe,
        title: "SSO Integration (SAML/OIDC)",
        description: "Enterprise single sign-on with SAML 2.0, OpenID Connect, and Active Directory integration.",
        metrics: "Enterprise SSO",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Fingerprint,
        title: "Multi-Factor Authentication",
        description: "Flexible MFA with authenticator apps, SMS, hardware tokens, and biometric options.",
        metrics: "Strong authentication",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Settings,
        title: "User Provisioning Automation",
        description: "Automated user lifecycle management with SCIM provisioning, role assignment, and deprovisioning.",
        metrics: "Automated provisioning",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: FileText,
        title: "Permission Templates",
        description: "Pre-defined role templates for common job functions with customisation and inheritance.",
        metrics: "Rapid role setup",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Workflow,
        title: "Access Request Workflows",
        description: "Self-service access requests with approval workflows, justification requirements, and audit trails.",
        metrics: "Controlled access",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Clock,
        title: "Session Management",
        description: "Active session monitoring with idle timeout, concurrent session limits, and forced logout capabilities.",
        metrics: "Session security",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Activity,
        title: "Login Audit Logs",
        description: "Comprehensive authentication logging with success/failure tracking, location, and device information.",
        metrics: "Complete login audit",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Lock,
        title: "Password Policy Enforcement",
        description: "Configurable password policies including complexity, rotation, history, and breach detection.",
        metrics: "Strong password policies",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: UserCheck,
        title: "Offboarding Automation",
        description: "Automated access revocation when employees leave with confirmation workflows and audit trails.",
        metrics: "Secure offboarding",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Shield,
        title: "Privileged Access Management",
        description: "Enhanced controls for administrative accounts with session recording and just-in-time access.",
        metrics: "Admin security",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: LayoutDashboard,
        title: "Identity Dashboard",
        description: "Centralised view of all users, roles, permissions, and access across systems.",
        metrics: "Identity visibility",
        color: "from-teal-500 to-cyan-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI-Powered Access Reviews",
        description: "Machine learning identifies anomalous access patterns and recommends permission adjustments.",
        metrics: "Intelligent access review",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Certification Campaigns",
        description: "Automated access review campaigns with manager attestation and remediation tracking.",
        metrics: "Access certification",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Share2,
        title: "Cross-System Sync",
        description: "Synchronise identities and permissions across contact centre, CRM, WFM, and enterprise systems.",
        metrics: "Unified identity",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: AlertTriangle,
        title: "Risk-Based Authentication",
        description: "Adaptive authentication that steps up security based on risk signals like location or device.",
        metrics: "Adaptive security",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: PieChart,
        title: "Access Analytics",
        description: "Analyse access patterns, identify over-privileged users, and track permission drift over time.",
        metrics: "Access intelligence",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Layers,
        title: "Entitlement Management",
        description: "Fine-grained entitlement management with attribute-based access control (ABAC) support.",
        metrics: "Flexible entitlements",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Gauge,
        title: "Segregation of Duties",
        description: "Prevent conflicts of interest with SoD rules, violation detection, and exception management.",
        metrics: "SoD enforcement",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Cpu,
        title: "API Gateway Integration",
        description: "Secure API access with OAuth 2.0, API keys, and fine-grained scope management.",
        metrics: "API security",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: RefreshCw,
        title: "Continuous Identity Verification",
        description: "Ongoing verification of user identity through behavioural biometrics and device trust.",
        metrics: "Continuous verification",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Lightbulb,
        title: "Role Mining & Optimisation",
        description: "Analyse existing access patterns to discover optimal role structures and reduce complexity.",
        metrics: "Role optimisation",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: LayoutDashboard,
        title: "Compliance Reporting",
        description: "Pre-built reports for SOX, GDPR, and regulatory compliance with evidence export.",
        metrics: "Compliance ready",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Search,
        title: "Access Search & Discovery",
        description: "Find who has access to what with powerful search across all systems and permissions.",
        metrics: "Access transparency",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with HSM key management',
        metrics: 'Enterprise encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Geo-redundant identity infrastructure with failover and guaranteed availability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Zero-Trust Architecture',
        description: 'Never trust, always verify approach with continuous authentication and authorisation',
        metrics: 'Zero-trust security',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Privileged Access Security',
        description: 'Enhanced controls for admin accounts with session recording, just-in-time access, and vault integration',
        metrics: 'Admin protection',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all identity operations, access decisions, and authentication events',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Secure Credential Storage',
        description: 'Encrypted credential storage with salted hashing, breach detection, and secure reset flows',
        metrics: 'Credential protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Threat Detection',
        description: 'AI-powered detection of credential stuffing, brute force, and account takeover attempts',
        metrics: 'Threat prevention',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'FIDO2/WebAuthn Support',
        description: 'Passwordless authentication with FIDO2 security keys and platform authenticators',
        metrics: 'Passwordless ready',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 15. AI Chatbot Builder
  'ai-chatbot-builder': {
    keyBenefits: [
      {
        icon: Bot,
        title: "No-Code Bot Builder",
        description: "Visual conversation designer with drag-drop interface—create sophisticated chatbots without coding.",
        metrics: "Zero coding required",
        color: "from-violet-500 to-indigo-600"
      },
      {
        icon: FileText,
        title: "Pre-Built Industry Templates",
        description: "100+ ready-to-deploy templates for retail, banking, telecom, healthcare, and more industries.",
        metrics: "100+ templates",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Brain,
        title: "NLU Intent Recognition",
        description: "Advanced natural language understanding with intent detection, entity extraction, and context management.",
        metrics: "95% intent accuracy",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Languages,
        title: "Multi-Language Support",
        description: "Deploy chatbots in 50+ languages with automatic detection and seamless language switching.",
        metrics: "50+ languages",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Globe,
        title: "Omnichannel Deployment",
        description: "Deploy once, run everywhere—web, mobile, WhatsApp, Facebook, Teams, and custom channels.",
        metrics: "All channels supported",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Users,
        title: "Human Handoff Logic",
        description: "Intelligent escalation to human agents with context transfer, sentiment triggers, and queue management.",
        metrics: "Seamless handoff",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: BarChart3,
        title: "Conversation Analytics",
        description: "Deep analytics on conversation flows, drop-off points, intent coverage, and containment rates.",
        metrics: "Complete analytics",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: GitBranch,
        title: "A/B Testing Framework",
        description: "Test different conversation flows, responses, and CTAs with statistical significance tracking.",
        metrics: "Optimise conversations",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Share2,
        title: "CRM Integration",
        description: "Native integration with Salesforce, HubSpot, and major CRMs for personalised conversations.",
        metrics: "CRM-connected",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: RefreshCw,
        title: "Continuous Learning",
        description: "AI improves automatically from conversations with human-in-the-loop training workflows.",
        metrics: "Self-improving AI",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Sparkles,
        title: "Generative AI Responses",
        description: "LLM-powered responses for complex queries with knowledge base grounding and guardrails.",
        metrics: "GPT-4 powered",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Search,
        title: "Knowledge Base Integration",
        description: "Connect to existing knowledge bases for instant, accurate answers to customer questions.",
        metrics: "Instant answers",
        color: "from-teal-500 to-cyan-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Workflow,
        title: "Visual Conversation Designer",
        description: "Flowchart-style conversation builder with conditions, loops, variables, and API calls.",
        metrics: "Complex logic support",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Cpu,
        title: "Custom Entity Training",
        description: "Train custom entities for your domain—product names, account numbers, policy types, and more.",
        metrics: "Domain-specific NLU",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: PieChart,
        title: "Sentiment-Aware Routing",
        description: "Detect customer frustration and automatically adjust tone, offer escalation, or change flow.",
        metrics: "Emotional intelligence",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Layers,
        title: "Multi-Turn Context Management",
        description: "Maintain conversation context across multiple turns with slot filling and anaphora resolution.",
        metrics: "Natural conversations",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Image,
        title: "Rich Media Support",
        description: "Send images, carousels, videos, files, and quick reply buttons in conversations.",
        metrics: "Rich interactions",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Calendar,
        title: "Appointment Scheduling",
        description: "Built-in scheduling capabilities with calendar integration and confirmation handling.",
        metrics: "Booking automation",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Wallet,
        title: "Payment Processing",
        description: "Secure in-chat payment collection with PCI compliance and multiple gateway support.",
        metrics: "In-chat payments",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Gauge,
        title: "Intent Confidence Tuning",
        description: "Fine-tune confidence thresholds for different intents to optimise accuracy vs. coverage.",
        metrics: "Precision control",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Share2,
        title: "Webhook & API Actions",
        description: "Call external APIs and webhooks to look up data, process transactions, or trigger actions.",
        metrics: "System integration",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: LayoutDashboard,
        title: "Bot Performance Dashboard",
        description: "Real-time monitoring of bot health, response times, containment, and customer satisfaction.",
        metrics: "Operational visibility",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: Lightbulb,
        title: "Training Recommendations",
        description: "AI identifies gaps in training data and recommends utterances to improve coverage.",
        metrics: "Guided improvement",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: GitBranch,
        title: "Version Control & Rollback",
        description: "Full version history with staging environments, rollback, and gradual deployment options.",
        metrics: "Safe deployments",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with secure AI model inference',
        metrics: 'Encrypted conversations',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Globally distributed bot infrastructure with auto-scaling and guaranteed availability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'PII Detection & Masking',
        description: 'Automatic detection and masking of personal information in conversations and logs',
        metrics: 'Data privacy protection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all user actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all bot configurations, deployments, and conversation data access',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'AI Guardrails',
        description: 'Configurable content filters, prohibited topic blocking, and response safety validation',
        metrics: 'Safe AI responses',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Conversation Data Retention',
        description: 'Configurable retention policies with automated purge and compliance with data protection regulations',
        metrics: 'Retention compliance',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 16. Screen Recording System
  'screen-recording-system': {
    keyBenefits: [
      {
        icon: Monitor,
        title: "100% Screen Capture",
        description: "Capture every agent desktop activity with high-quality video, configurable frame rates, and compression.",
        metrics: "Complete visibility",
        color: "from-slate-500 to-gray-600"
      },
      {
        icon: PhoneCall,
        title: "Call-Sync Recording",
        description: "Synchronised playback of screen recording with call audio for complete interaction review.",
        metrics: "Audio-visual sync",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Settings,
        title: "Application Tracking",
        description: "Automatic detection and logging of applications used during customer interactions.",
        metrics: "App usage insights",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Clock,
        title: "Idle Time Detection",
        description: "Identify and flag periods of inactivity with configurable thresholds and alerting.",
        metrics: "Productivity tracking",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Eye,
        title: "PII Masking",
        description: "Automatic detection and masking of sensitive data like card numbers and SSNs on screen.",
        metrics: "Data protection",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Lock,
        title: "Secure Playback",
        description: "Role-based access to recordings with encrypted storage and playback watermarking.",
        metrics: "Controlled access",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Search,
        title: "Search & Filter",
        description: "Find specific recordings by agent, date, call ID, customer, or application used.",
        metrics: "Instant search",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: FileText,
        title: "Annotation Tools",
        description: "Add bookmarks, notes, and timestamps during playback for training and QA review.",
        metrics: "Coaching ready",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Share2,
        title: "Clip Export & Sharing",
        description: "Create and share specific clips for coaching, training, or compliance evidence.",
        metrics: "Easy sharing",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: BarChart3,
        title: "Desktop Analytics Dashboard",
        description: "Insights into application usage patterns, process adherence, and productivity metrics.",
        metrics: "Desktop intelligence",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Activity,
        title: "Process Compliance Monitoring",
        description: "Verify agents follow correct procedures with step sequence tracking and deviation alerts.",
        metrics: "Process adherence",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Award,
        title: "QA Integration",
        description: "Native integration with quality evaluation workflows for seamless review processes.",
        metrics: "QA connected",
        color: "from-teal-500 to-cyan-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Process Analysis",
        description: "Machine learning analyses screen recordings to identify best practices and process improvements.",
        metrics: "Automated insights",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Robotic Process Analytics",
        description: "Identify automation opportunities by analysing repetitive tasks across screen recordings.",
        metrics: "RPA discovery",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: ScreenShare,
        title: "Multi-Monitor Support",
        description: "Capture activity across multiple monitors with unified or split-screen playback options.",
        metrics: "Multi-screen capture",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: MousePointer,
        title: "Mouse & Keyboard Tracking",
        description: "Log keystrokes and mouse movements for detailed activity analysis and process mapping.",
        metrics: "Input tracking",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Play,
        title: "Speed Playback Controls",
        description: "Review recordings at 2x, 4x, or 8x speed with automatic pause on important events.",
        metrics: "Efficient review",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: AlertTriangle,
        title: "Policy Violation Detection",
        description: "Automatic flagging of policy violations like unauthorised application use or data access.",
        metrics: "Compliance monitoring",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Layers,
        title: "Recording Policies",
        description: "Configurable recording rules based on queue, skill, agent, or call type with scheduling.",
        metrics: "Selective recording",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Gauge,
        title: "Storage Optimisation",
        description: "Intelligent compression, tiered storage, and automatic archival to manage storage costs.",
        metrics: "Cost-effective storage",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: PieChart,
        title: "Agent Comparison Analytics",
        description: "Compare agent workflows and identify efficiency differences for training purposes.",
        metrics: "Best practice discovery",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Pause,
        title: "Privacy Pause Controls",
        description: "Automatic or manual recording pause for sensitive data entry with compliance logging.",
        metrics: "Privacy protection",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: RefreshCw,
        title: "Real-Time Monitoring Mode",
        description: "Live view of agent screens for immediate support, training, and supervision.",
        metrics: "Live monitoring",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Share2,
        title: "API & Integration",
        description: "REST APIs for integration with WFM, QA, and training systems for automated workflows.",
        metrics: "System integration",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for recordings at rest and TLS 1.3 for transmission with secure key management',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Highly available recording infrastructure with no data loss guarantee',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Automatic PII Masking',
        description: 'AI-powered detection and masking of sensitive data including card numbers and personal identifiers',
        metrics: 'Real-time PII protection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all playback',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all recording access, playback, export, and deletion activities',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Secure Storage Infrastructure',
        description: 'Geo-redundant encrypted storage with configurable retention and automated secure deletion',
        metrics: 'Protected recordings',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Playback Watermarking',
        description: 'Dynamic watermarks on playback showing viewer identity for leak prevention and accountability',
        metrics: 'Leak prevention',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Agent Privacy Controls',
        description: 'Configurable recording boundaries with break time exclusions and consent management',
        metrics: 'Privacy compliance',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 17. Live Agent Assist
  'live-agent-assist': {
    keyBenefits: [
      {
        icon: Sparkles,
        title: "Real-Time AI Suggestions",
        description: "AI analyses conversations and provides instant response suggestions, next-best-actions, and talking points.",
        metrics: "Sub-second suggestions",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Search,
        title: "Contextual Knowledge Search",
        description: "AI surfaces relevant knowledge articles, policies, and procedures based on conversation context.",
        metrics: "Instant knowledge access",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: AlertTriangle,
        title: "Compliance Guardrails",
        description: "Real-time alerts for compliance risks, required disclosures, and prohibited language.",
        metrics: "100% compliance protection",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Smile,
        title: "Sentiment Monitoring",
        description: "Live sentiment tracking with alerts when customer frustration rises and de-escalation suggestions.",
        metrics: "Emotional awareness",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Zap,
        title: "Auto-Fill & Shortcuts",
        description: "AI auto-completes common responses, fills forms, and provides keyboard shortcuts for efficiency.",
        metrics: "3x faster handling",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Workflow,
        title: "Process Guidance",
        description: "Step-by-step guidance through complex procedures with dynamic branching based on customer needs.",
        metrics: "Guided resolution",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Languages,
        title: "Real-Time Translation",
        description: "Live translation of customer messages and suggested responses in 100+ languages.",
        metrics: "100+ languages",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Brain,
        title: "Customer History Summary",
        description: "AI-generated summary of customer history, recent interactions, and key context at conversation start.",
        metrics: "Instant context",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Target,
        title: "Upsell & Cross-Sell Prompts",
        description: "AI identifies opportunities and provides personalised product recommendations with success scripts.",
        metrics: "Revenue optimisation",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Clock,
        title: "Handle Time Coaching",
        description: "Real-time indicators and suggestions to help agents manage conversation length efficiently.",
        metrics: "AHT optimisation",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: FileText,
        title: "Auto-Summary Generation",
        description: "AI generates call summaries, notes, and disposition codes automatically at conversation end.",
        metrics: "Automated wrap-up",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: Users,
        title: "Supervisor Whisper Integration",
        description: "Seamless integration with supervisor whisper allowing AI and human coaching simultaneously.",
        metrics: "Dual coaching support",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: "Custom AI Model Training",
        description: "Train AI on your products, policies, and successful agent patterns for highly relevant suggestions.",
        metrics: "Domain-specific AI",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: PieChart,
        title: "Suggestion Acceptance Analytics",
        description: "Track which suggestions agents accept, reject, or modify to continuously improve AI accuracy.",
        metrics: "Learning loop",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Layers,
        title: "Multi-Channel Support",
        description: "AI assistance across voice, chat, email, and social with channel-appropriate suggestions.",
        metrics: "Omnichannel assist",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Gauge,
        title: "Confidence Scoring",
        description: "AI shows confidence levels for suggestions allowing agents to prioritise high-quality recommendations.",
        metrics: "Transparent AI",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: RefreshCw,
        title: "Continuous Learning",
        description: "AI improves from agent feedback with reinforcement learning from human corrections.",
        metrics: "Self-improving AI",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Share2,
        title: "CRM Integration",
        description: "Pull customer data and push updates to CRM automatically during conversations.",
        metrics: "CRM connected",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Lightbulb,
        title: "Agent Coaching Insights",
        description: "Track individual agent AI usage patterns to identify coaching opportunities.",
        metrics: "Performance insights",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: LayoutDashboard,
        title: "Supervisor Dashboard",
        description: "Real-time view of AI assistance across all agents with intervention capabilities.",
        metrics: "Team oversight",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: FileSearch,
        title: "Knowledge Gap Detection",
        description: "AI identifies questions the knowledge base cannot answer for continuous content improvement.",
        metrics: "KB improvement",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Workflow,
        title: "Workflow Automation Triggers",
        description: "AI can trigger automations like ticket creation, escalation, or callback scheduling.",
        metrics: "Process automation",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: Bell,
        title: "Priority Alert System",
        description: "Highlight critical suggestions and alerts that require immediate attention from agents.",
        metrics: "Priority focus",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: GitBranch,
        title: "A/B Testing Framework",
        description: "Test different AI models and suggestion strategies to optimise performance.",
        metrics: "Continuous optimisation",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with secure AI inference',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Highly available AI infrastructure with sub-second response times and guaranteed availability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Data Isolation',
        description: 'Complete isolation of customer data with no cross-tenant AI training or data leakage',
        metrics: 'Tenant isolation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all AI interactions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all AI suggestions, agent actions, and system events',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'AI Guardrails',
        description: 'Configurable content filters and safety validation ensuring appropriate AI suggestions',
        metrics: 'Safe AI assistance',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'PCI-DSS Compliant',
        description: 'AI assistance operates with PCI-DSS compliance including automatic payment data exclusion',
        metrics: 'Payment data safety',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 18. Gamification Engine
  'gamification-engine': {
    keyBenefits: [
      {
        icon: Award,
        title: "Points & Rewards System",
        description: "Comprehensive point system with customisable earning rules, multipliers, and redemption options.",
        metrics: "Unlimited point structures",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Target,
        title: "Achievement Badges",
        description: "Design and award badges for milestones, skills, and exceptional performance with display options.",
        metrics: "Custom badge creation",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: BarChart3,
        title: "Live Leaderboards",
        description: "Real-time leaderboards with multiple views—daily, weekly, monthly, and all-time rankings.",
        metrics: "Real-time rankings",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: TrendingUp,
        title: "Progress Tracking",
        description: "Visual progress indicators for goals, levels, and achievements with personalised dashboards.",
        metrics: "Clear progress visibility",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Users,
        title: "Team Competitions",
        description: "Create team-based challenges with collective goals, team leaderboards, and shared rewards.",
        metrics: "Team engagement",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Bell,
        title: "Instant Recognition",
        description: "Real-time notifications for achievements, badges, and milestones with social sharing options.",
        metrics: "Instant gratification",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Calendar,
        title: "Scheduled Challenges",
        description: "Create time-based challenges with start/end dates, progressive difficulty, and seasonal themes.",
        metrics: "Campaign management",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: CreditCard,
        title: "Reward Marketplace",
        description: "Built-in marketplace where agents can redeem points for gifts, time off, or experiences.",
        metrics: "Tangible rewards",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Monitor,
        title: "Wallboard Display",
        description: "Optimised displays for contact floor TVs with animations, celebrations, and live updates.",
        metrics: "Visual motivation",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Laptop,
        title: "Mobile App Access",
        description: "Full gamification experience on mobile devices for remote agents and on-the-go engagement.",
        metrics: "Mobile engagement",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Sparkles,
        title: "Celebration Animations",
        description: "Visual celebrations and sound effects when agents hit milestones or earn achievements.",
        metrics: "Fun factor",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: PieChart,
        title: "Engagement Analytics",
        description: "Track gamification effectiveness with participation rates, motivation impact, and ROI metrics.",
        metrics: "Measure success",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI-Personalised Challenges",
        description: "AI creates individualised challenges based on each agent's skills, goals, and performance patterns.",
        metrics: "Personalised motivation",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Workflow,
        title: "Behaviour-Based Triggers",
        description: "Award points and badges automatically based on performance data, quality scores, and achievements.",
        metrics: "Automated gamification",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Share2,
        title: "WFM & QA Integration",
        description: "Pull data from workforce management and quality systems for comprehensive gamification.",
        metrics: "System integration",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Layers,
        title: "Multi-Tier Levels",
        description: "Create progression systems with levels, ranks, and tiers that unlock new rewards and privileges.",
        metrics: "Career progression",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: GitBranch,
        title: "Challenge Templates",
        description: "Pre-built challenge templates for common scenarios with easy customisation and deployment.",
        metrics: "Quick setup",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: HeartHandshake,
        title: "Peer Recognition",
        description: "Enable agents to recognise colleagues with kudos, thank-you notes, and peer-nominated awards.",
        metrics: "Social recognition",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Gauge,
        title: "Fair Play Algorithms",
        description: "Balancing algorithms ensure fair competition across different shifts, skills, and tenure levels.",
        metrics: "Fair competition",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: LayoutDashboard,
        title: "Admin Control Centre",
        description: "Complete administrative control over all gamification elements with preview and testing tools.",
        metrics: "Full control",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: RefreshCw,
        title: "A/B Testing",
        description: "Test different gamification strategies and measure impact on performance and engagement.",
        metrics: "Optimise approach",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Lightbulb,
        title: "Suggestion Engine",
        description: "AI recommends new challenges, badges, and rewards based on engagement patterns and trends.",
        metrics: "Smart recommendations",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: FileText,
        title: "Impact Reporting",
        description: "Detailed reports on gamification impact on KPIs, retention, and employee satisfaction.",
        metrics: "ROI measurement",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Globe,
        title: "Multi-Language Support",
        description: "Full localisation in 30+ languages for global gamification deployment.",
        metrics: "Global reach",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with secure authentication',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Highly available platform with real-time updates and guaranteed availability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Fair Play Protection',
        description: 'Anti-gaming measures prevent point manipulation, badge exploitation, and unfair advantages',
        metrics: 'Integrity protection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all point awards, redemptions, and administrative changes',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Real-Time Sync',
        description: 'Instant synchronisation of achievements and leaderboards across all devices and locations',
        metrics: 'Instant updates',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Privacy Compliance',
        description: 'Employee data protection with consent management and opt-out options for privacy compliance',
        metrics: 'Privacy respecting',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 19. Dialler Campaign Manager
  'dialler-campaign-manager': {
    keyBenefits: [
      {
        icon: PhoneOutgoing,
        title: "Multi-Mode Dialler",
        description: "Preview, progressive, predictive, and power dialling modes with intelligent switching based on conditions.",
        metrics: "All dial modes",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Workflow,
        title: "Campaign Management",
        description: "Create, schedule, and manage multiple campaigns with priority queuing and resource allocation.",
        metrics: "Unlimited campaigns",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Users,
        title: "List Management",
        description: "Import, segment, and manage calling lists with deduplication, DNC checking, and list washing.",
        metrics: "Smart list handling",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Brain,
        title: "AI Pacing Algorithm",
        description: "Machine learning optimises dial pacing in real-time based on agent availability and answer rates.",
        metrics: "Optimal connect rates",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Shield,
        title: "TCPA & OFCOM Compliance",
        description: "Built-in compliance controls for calling hours, abandon rates, DNC, and regulatory requirements.",
        metrics: "Full regulatory compliance",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: BarChart3,
        title: "Real-Time Campaign Analytics",
        description: "Live dashboards showing connect rates, conversion metrics, agent performance, and campaign ROI.",
        metrics: "Live campaign insights",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: Timer,
        title: "Smart Callback Scheduling",
        description: "Automated callback scheduling with optimal timing predictions and agent assignment.",
        metrics: "Intelligent callbacks",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: Phone,
        title: "AMD/Answering Machine Detection",
        description: "Accurate voicemail detection with configurable actions including message drops and callbacks.",
        metrics: "95% AMD accuracy",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: MessageSquare,
        title: "Pre-Recorded Voicemail",
        description: "Drop pre-recorded voicemails with personalisation and regulatory-compliant disclosures.",
        metrics: "Voicemail automation",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Calendar,
        title: "Time Zone Management",
        description: "Automatic calling window enforcement based on customer time zones with holiday handling.",
        metrics: "Global compliance",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: Target,
        title: "Lead Scoring Integration",
        description: "Prioritise calls based on lead scores with dynamic list reordering and hot lead alerts.",
        metrics: "Smart prioritisation",
        color: "from-teal-500 to-cyan-600"
      },
      {
        icon: FileText,
        title: "Script Management",
        description: "Dynamic call scripts with branching logic, compliance prompts, and auto-field population.",
        metrics: "Guided conversations",
        color: "from-yellow-500 to-orange-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: "Best Time to Call AI",
        description: "AI predicts optimal contact times for each lead based on historical patterns and demographics.",
        metrics: "40% higher contact rates",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: GitBranch,
        title: "A/B Testing Framework",
        description: "Test different scripts, timing strategies, and campaign parameters with statistical analysis.",
        metrics: "Data-driven optimisation",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: Share2,
        title: "CRM Bi-Directional Sync",
        description: "Real-time sync with CRM for lead updates, disposition recording, and opportunity management.",
        metrics: "CRM connected",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Layers,
        title: "Multi-Channel Campaigns",
        description: "Coordinate outbound across voice, SMS, email, and digital for omnichannel customer engagement.",
        metrics: "Omnichannel outbound",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: PieChart,
        title: "Conversion Analytics",
        description: "Track full-funnel conversion with attribution, revenue tracking, and campaign ROI calculation.",
        metrics: "Revenue attribution",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Gauge,
        title: "Agent Blending",
        description: "Dynamically blend inbound and outbound based on queue conditions and staffing levels.",
        metrics: "Optimal utilisation",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: RefreshCw,
        title: "Retry Strategy Engine",
        description: "Configurable retry rules based on outcome codes with exponential backoff and attempt limits.",
        metrics: "Smart retry logic",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: LayoutDashboard,
        title: "Campaign Control Centre",
        description: "Real-time campaign management with pause, resume, scale, and emergency stop controls.",
        metrics: "Full control",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Lightbulb,
        title: "Propensity Modelling",
        description: "AI scores leads on likelihood to convert, answer, or purchase for intelligent targeting.",
        metrics: "Predictive targeting",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Workflow,
        title: "Automated List Refreshing",
        description: "Scheduled list imports, transformations, and enrichment from external data sources.",
        metrics: "Fresh data",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: AlertTriangle,
        title: "DNC Management",
        description: "Comprehensive Do Not Call list management with internal, national, and state list integration.",
        metrics: "DNC compliance",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: FileSearch,
        title: "Compliance Audit Reports",
        description: "Detailed compliance reporting for regulators with call attempt logs and consent tracking.",
        metrics: "Audit ready",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with secure dialler infrastructure',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Carrier-grade dialler infrastructure with geo-redundancy and guaranteed availability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'TCPA/OFCOM Compliant',
        description: 'Built-in compliance engine ensuring adherence to all telemarketing regulations and calling laws',
        metrics: 'Regulatory compliance',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all actions',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all dial attempts, outcomes, and campaign changes for compliance',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'DNC List Security',
        description: 'Encrypted DNC list storage with access logging and automatic regulatory list updates',
        metrics: 'DNC protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Fraud Prevention',
        description: 'Anti-fraud measures including call pattern analysis, velocity controls, and suspicious activity alerts',
        metrics: 'Fraud detection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Consent Management',
        description: 'Track and enforce consent preferences with opt-in/opt-out management and audit trails',
        metrics: 'Consent compliance',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // 20. Knowledge Management Hub
  'knowledge-management-hub': {
    keyBenefits: [
      {
        icon: Search,
        title: "AI-Powered Search",
        description: "Natural language search with intent understanding, semantic matching, and federated search across sources.",
        metrics: "95% search accuracy",
        color: "from-cyan-500 to-blue-600"
      },
      {
        icon: FileText,
        title: "Content Management System",
        description: "Full-featured CMS with rich text editing, multimedia support, versioning, and approval workflows.",
        metrics: "Complete authoring",
        color: "from-purple-500 to-indigo-600"
      },
      {
        icon: Layers,
        title: "Category & Taxonomy",
        description: "Flexible categorisation with hierarchical taxonomies, tags, and automatic classification.",
        metrics: "Organised knowledge",
        color: "from-blue-500 to-cyan-600"
      },
      {
        icon: Workflow,
        title: "Review & Approval Workflows",
        description: "Configurable approval processes with SME review, compliance checking, and scheduled publishing.",
        metrics: "Controlled publishing",
        color: "from-emerald-500 to-teal-600"
      },
      {
        icon: Clock,
        title: "Content Expiration & Review",
        description: "Automated content review reminders, expiration alerts, and archival workflows.",
        metrics: "Fresh content",
        color: "from-amber-500 to-orange-600"
      },
      {
        icon: Users,
        title: "Role-Based Access",
        description: "Control who can view, edit, approve, and publish content with hierarchical permissions.",
        metrics: "Secure access",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: BarChart3,
        title: "Usage Analytics",
        description: "Track article views, searches, helpfulness ratings, and identify knowledge gaps.",
        metrics: "Usage insights",
        color: "from-pink-500 to-rose-600"
      },
      {
        icon: MessageSquare,
        title: "Feedback & Ratings",
        description: "Collect user feedback with ratings, comments, and improvement suggestions on articles.",
        metrics: "Continuous improvement",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Share2,
        title: "Agent Desktop Integration",
        description: "Seamless integration with agent desktops for contextual article suggestions during interactions.",
        metrics: "In-context knowledge",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: Globe,
        title: "Customer Self-Service Portal",
        description: "Public-facing knowledge base with search, FAQ, and guided troubleshooting for customers.",
        metrics: "Self-service enablement",
        color: "from-indigo-500 to-violet-600"
      },
      {
        icon: Languages,
        title: "Multi-Language Support",
        description: "Create and manage content in multiple languages with translation workflows and localisation.",
        metrics: "50+ languages",
        color: "from-orange-500 to-amber-600"
      },
      {
        icon: FileSearch,
        title: "Content Templates",
        description: "Pre-built templates for common article types with guided creation and consistency enforcement.",
        metrics: "Standardised content",
        color: "from-teal-500 to-cyan-600"
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: "AI Content Generation",
        description: "Generate article drafts from conversation logs, tickets, or subject matter expert input using AI.",
        metrics: "Accelerated authoring",
        color: "from-purple-500 to-pink-600"
      },
      {
        icon: Sparkles,
        title: "Knowledge Gap Detection",
        description: "AI identifies missing knowledge based on search queries, agent questions, and ticket analysis.",
        metrics: "Proactive gap filling",
        color: "from-green-500 to-emerald-600"
      },
      {
        icon: RefreshCw,
        title: "Auto-Update Suggestions",
        description: "AI recommends content updates based on process changes, new products, or accuracy issues.",
        metrics: "Smart maintenance",
        color: "from-blue-500 to-indigo-600"
      },
      {
        icon: Target,
        title: "Contextual Recommendations",
        description: "AI surfaces relevant articles based on conversation context, customer profile, and issue type.",
        metrics: "Right knowledge, right time",
        color: "from-cyan-500 to-teal-600"
      },
      {
        icon: Lightbulb,
        title: "Decision Tree Builder",
        description: "Create interactive troubleshooting guides with branching logic and outcome tracking.",
        metrics: "Guided resolution",
        color: "from-amber-500 to-yellow-600"
      },
      {
        icon: Video,
        title: "Multimedia Support",
        description: "Embed videos, images, diagrams, and interactive elements in knowledge articles.",
        metrics: "Rich content",
        color: "from-red-500 to-rose-600"
      },
      {
        icon: GitBranch,
        title: "Version Control & History",
        description: "Full version history with comparison, rollback, and change tracking for all content.",
        metrics: "Complete history",
        color: "from-violet-500 to-purple-600"
      },
      {
        icon: PieChart,
        title: "Content Effectiveness Analytics",
        description: "Measure article impact on FCR, handle time, and customer satisfaction with A/B testing.",
        metrics: "Impact measurement",
        color: "from-indigo-500 to-blue-600"
      },
      {
        icon: Cpu,
        title: "API & Chatbot Integration",
        description: "REST APIs for integration with chatbots, IVR systems, and third-party applications.",
        metrics: "Universal access",
        color: "from-emerald-500 to-green-600"
      },
      {
        icon: Workflow,
        title: "Automated Content Tagging",
        description: "AI automatically suggests tags, categories, and related articles during content creation.",
        metrics: "Smart organisation",
        color: "from-orange-500 to-red-600"
      },
      {
        icon: LayoutDashboard,
        title: "Author Dashboard",
        description: "Personal dashboard for authors showing their content, pending reviews, and performance metrics.",
        metrics: "Author productivity",
        color: "from-gray-500 to-slate-600"
      },
      {
        icon: Scale,
        title: "Compliance Article Tracking",
        description: "Special workflows for compliance-critical content with mandatory reviews and approval chains.",
        metrics: "Compliance content",
        color: "from-pink-500 to-rose-600"
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'SOC 2 Type II & ISO 27001 Certified',
        description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and GDPR requirements',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'End-to-End Encryption',
        description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with secure content storage',
        metrics: 'Military-grade encryption',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Rocket,
        title: '99.99% Uptime SLA',
        description: 'Highly available knowledge platform with global CDN and guaranteed availability',
        metrics: '99.99% uptime guaranteed',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Shield,
        title: 'Content Security Classification',
        description: 'Tag and control access to sensitive content with security classifications and handling rules',
        metrics: 'Classified content protection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Role-Based Access Control',
        description: 'Granular permissions with SSO integration, MFA enforcement, and audit logging for all content access',
        metrics: 'Zero-trust access model',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Complete Audit Trails',
        description: 'Comprehensive logging of all content views, edits, approvals, and exports for compliance',
        metrics: 'Full audit visibility',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Geo-Redundant Data Storage',
        description: 'Multi-region data replication with configurable data residency controls for regional compliance',
        metrics: 'Global data protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Network,
        title: 'Search Security',
        description: 'Secure search respecting access permissions with no data leakage between security contexts',
        metrics: 'Secure search',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Content Backup & Recovery',
        description: 'Automated backups with point-in-time recovery and disaster recovery procedures',
        metrics: 'Content protection',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};
